import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { ArrowLeft, FileDown, Plus, Minus, Check, Trash2, Send, Search, ChevronDown, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = ['Belgie','Nederland','Deutschland','France','Luxembourg','United Kingdom','Osterreich','Schweiz','Italia','Espana','Portugal','Danmark','Sverige','Norge','Suomi','Polska','Cesko','Magyarorszag','Romania','Bulgaria','Hrvatska','Slovenija','Slovensko','Lietuva','Latvija','Eesti','Ireland','Turkiye','United States','Canada','Maroc','Tunisie'];

const CountrySelect = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);
  React.useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const filtered = COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} className={`w-full px-3 py-2.5 border text-sm text-left flex items-center justify-between bg-white ${open ? 'border-[#BE1622]' : 'border-gray-200'}`}>
        <span className={value ? 'text-slate-900' : 'text-slate-400'}>{value || placeholder}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg max-h-44 flex flex-col">
          <div className="flex items-center border-b border-gray-100 px-3 py-2">
            <Search className="w-3.5 h-3.5 text-slate-400 mr-2" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="..." className="w-full text-sm focus:outline-none" autoFocus />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map(c => (
              <button key={c} type="button" onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${value === c ? 'text-[#BE1622] font-medium' : 'text-slate-700'}`}>{c}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ProductDetailPage = () => {
  const { language, getLocalizedField } = useLanguage();
  const { items, addItem, updateItem, removeItem, clearCart } = useCart();
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [orderType, setOrderType] = useState('pallet');
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [formData, setFormData] = useState({ firstName: '', lastName: '', company: '', btw: '', email: '', phone: '', country: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const pickerRef = React.useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [pRes, prodsRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/products`)
        ]);
        setProduct(pRes.data);
        setAllProducts(prodsRes.data);
        if (pRes.data.category_id) {
          const catRes = await axios.get(`${API}/categories/${pRes.data.category_id}`);
          setCategory(catRes.data);
          setSiblings(prodsRes.data.filter(p => p.category_id === pRes.data.category_id && p.id !== productId));
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [productId]);

  useEffect(() => {
    if (!product?.id) return;
    const allIds = [product.id, ...siblings.map(s => s.id)];
    Promise.all(allIds.map(pid => axios.get(`${API}/certificates/product/${pid}`).catch(() => ({ data: [] }))))
      .then(results => {
        const seen = new Set(); const all = [];
        results.forEach(r => (r.data || []).forEach(c => { if (!seen.has(c.id)) { seen.add(c.id); all.push(c); } }));
        setCertificates(all);
      });
  }, [product?.id, siblings]);

  useEffect(() => {
    const close = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowProductPicker(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const variantGroup = useMemo(() => {
    if (!product) return { variants: [], base: '' };
    const name = product[`name_${language}`] || product.name_nl || '';
    const base = name.replace(/\s*L=[\d,]+M$/i, '').replace(/\s*[\d,]+x[\d,]+(mm)?$/i, '').trim();
    const variants = siblings.filter(s => (s[`name_${language}`] || s.name_nl || '').startsWith(base))
      .map(s => ({ label: (s[`name_${language}`] || s.name_nl || '').replace(base, '').trim(), product: s }));
    return { base, variants: [{ label: name.replace(base, '').trim() || name, product }, ...variants] };
  }, [product, siblings, language]);

  const recommended = useMemo(() => {
    if (!product) return [];
    const groups = {};
    siblings.forEach(s => {
      const name = s[`name_${language}`] || s.name_nl || '';
      const base = name.replace(/\s*L=[\d,]+M$/i, '').replace(/\s*[\d,]+x[\d,]+(mm)?$/i, '').trim();
      if (base !== variantGroup.base && !groups[base]) groups[base] = s;
    });
    return Object.values(groups).slice(0, 4);
  }, [siblings, product, language, variantGroup.base]);

  const activeProduct = variantGroup.variants[selectedVariant]?.product || product;
  const activeSpecs = activeProduct?.specifications || [];

  const filteredPickerProducts = allProducts.filter(p =>
    (p[`name_${language}`] || p.name_nl || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  const handleAddToCart = () => {
    if (!activeProduct) return;
    addItem(activeProduct, variantGroup.variants[selectedVariant]?.label || '', orderType, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleAddFromPicker = (p) => {
    addItem(p, '', 'pallet', 1);
    setShowProductPicker(false);
    setProductSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSending(true);
    const lines = items.map((it, i) => `${i + 1}. ${it.productName} | ${it.orderType === 'truck' ? 'Vrachtwagen' : 'Palet'} | Qty: ${it.quantity}`).join('\n');
    try {
      await axios.post(`${API}/contact`, {
        name: `${formData.firstName} ${formData.lastName}`, email: formData.email, phone: formData.phone, company: formData.company,
        message: `[OFFERTE]\nBTW: ${formData.btw}\nLand: ${formData.country}\n\n${lines}\n\n${formData.message}`
      });
      setSubmitted(true);
      clearCart();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const tx = {
    nl: { back: 'Terug naar producten', variants: 'AFMETINGEN', specs: 'SPECIFICATIES', cert: 'CERTIFICATEN', addToQuote: 'Toevoegen aan offerte', added: 'Toegevoegd!', pallet: 'Palet', truck: 'Vrachtwagen', orderType: 'BESTELVORM', qty: 'AANTAL', yourQuote: 'UW OFFERTE', addMore: 'Product toevoegen', searchProd: 'Zoek product...', sendQuote: 'Offerte versturen', contact: 'UW GEGEVENS', firstName: 'Voornaam', lastName: 'Achternaam', company: 'Bedrijf', btw: 'BTW Nummer', email: 'E-mail', phone: 'Telefoon', country: 'Selecteer land', msg: 'Opmerkingen', submit: 'OFFERTE VERSTUREN', success: 'Uw offerte is verzonden!', successDesc: 'Wij nemen zo snel mogelijk contact met u op.', recommended: 'GERELATEERDE PRODUCTEN' },
    en: { back: 'Back to products', variants: 'DIMENSIONS', specs: 'SPECIFICATIONS', cert: 'CERTIFICATES', addToQuote: 'Add to quote', added: 'Added!', pallet: 'Pallet', truck: 'Truck', orderType: 'ORDER TYPE', qty: 'QUANTITY', yourQuote: 'YOUR QUOTE', addMore: 'Add product', searchProd: 'Search product...', sendQuote: 'Send quote', contact: 'YOUR DETAILS', firstName: 'First name', lastName: 'Last name', company: 'Company', btw: 'VAT Number', email: 'Email', phone: 'Phone', country: 'Select country', msg: 'Comments', submit: 'SEND QUOTE REQUEST', success: 'Your quote has been sent!', successDesc: 'We will contact you as soon as possible.', recommended: 'RELATED PRODUCTS' },
    fr: { back: 'Retour aux produits', variants: 'DIMENSIONS', specs: 'SPECIFICATIONS', cert: 'CERTIFICATS', addToQuote: 'Ajouter au devis', added: 'Ajoute!', pallet: 'Palette', truck: 'Camion', orderType: 'TYPE DE COMMANDE', qty: 'QUANTITE', yourQuote: 'VOTRE DEVIS', addMore: 'Ajouter un produit', searchProd: 'Rechercher...', sendQuote: 'Envoyer', contact: 'VOS COORDONNEES', firstName: 'Prenom', lastName: 'Nom', company: 'Entreprise', btw: 'Numero TVA', email: 'E-mail', phone: 'Telephone', country: 'Selectionnez', msg: 'Remarques', submit: 'ENVOYER LE DEVIS', success: 'Votre devis a ete envoye!', successDesc: 'Nous vous contacterons rapidement.', recommended: 'PRODUITS ASSOCIES' },
    de: { back: 'Zuruck zu Produkten', variants: 'ABMESSUNGEN', specs: 'SPEZIFIKATIONEN', cert: 'ZERTIFIKATE', addToQuote: 'Zum Angebot', added: 'Hinzugefugt!', pallet: 'Palette', truck: 'LKW', orderType: 'BESTELLART', qty: 'MENGE', yourQuote: 'IHR ANGEBOT', addMore: 'Produkt hinzufugen', searchProd: 'Suchen...', sendQuote: 'Senden', contact: 'IHRE DATEN', firstName: 'Vorname', lastName: 'Nachname', company: 'Unternehmen', btw: 'USt-IdNr.', email: 'E-Mail', phone: 'Telefon', country: 'Land', msg: 'Bemerkungen', submit: 'ANGEBOT ABSENDEN', success: 'Ihr Angebot wurde gesendet!', successDesc: 'Wir melden uns schnellstmoglich.', recommended: 'VERWANDTE PRODUKTE' },
  }[language] || {};

  if (loading) return <div className="pt-28 min-h-screen flex justify-center"><div className="w-7 h-7 border-2 border-[#BE1622] border-t-transparent rounded-full animate-spin mt-20" /></div>;
  if (!product) return <div className="pt-28 min-h-screen text-center"><p className="text-slate-400 mt-20">Not found</p></div>;

  return (
    <div data-testid="product-detail-page">
      <section className="bg-[#0F172A] pt-28 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center text-xs text-white/40 hover:text-white transition-colors mb-3"><ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> {tx.back}</Link>
          <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">{variantGroup.base || getLocalizedField(product, 'name')}</h1>
          {category && <p className="text-white/40 text-xs mt-1">{getLocalizedField(category, 'name')}</p>}
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* LEFT - Product info */}
            <div>
              <div className="bg-[#F8FAFC] p-10 flex items-center justify-center mb-6">
                <img src={activeProduct?.image_url || product.image_url || ''} alt="" className="max-w-full max-h-64 object-contain" />
              </div>
              {getLocalizedField(activeProduct || product, 'description') && (
                <p className="text-slate-600 text-sm leading-relaxed mb-6">{getLocalizedField(activeProduct || product, 'description')}</p>
              )}
              {variantGroup.variants.length > 1 && (
                <div className="mb-6">
                  <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-2">{tx.variants}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {variantGroup.variants.map((v, vi) => (
                      <button key={vi} onClick={() => setSelectedVariant(vi)}
                        className={`px-3 py-1.5 text-xs font-medium border transition-all ${selectedVariant === vi ? 'border-[#BE1622] bg-[#BE1622] text-white' : 'border-gray-200 text-slate-600 hover:border-slate-400'}`}>{v.label}</button>
                    ))}
                  </div>
                </div>
              )}
              {activeSpecs.length > 0 && (
                <div className="mb-6">
                  <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-2">{tx.specs}</p>
                  <div className="border border-gray-200">
                    {activeSpecs.map((s, si) => (
                      <div key={si} className={`flex text-xs ${si % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                        <span className="w-1/2 px-3 py-2 text-slate-500">{s[`key_${language}`] || s.key_nl}</span>
                        <span className="w-1/2 px-3 py-2 text-slate-900 font-medium">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {certificates.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-2">{tx.cert}</p>
                  <div className="space-y-2">
                    {certificates.map((cert, ci) => (
                      <a key={ci} href={`${process.env.REACT_APP_BACKEND_URL}${cert.file_url}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-gray-200 hover:border-[#BE1622] transition-colors group" data-testid={`cert-${ci}`}>
                        <div className="w-8 h-8 bg-[#BE1622]/10 flex items-center justify-center flex-shrink-0"><FileDown className="w-4 h-4 text-[#BE1622]" /></div>
                        <div><span className="text-sm font-medium text-slate-800 group-hover:text-[#BE1622] block">{cert.name}</span><span className="text-[10px] text-slate-400">PDF</span></div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT - Offerte panel */}
            <div>
              <div className="border border-gray-200 sticky top-24">
                {submitted ? (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-3"><Send className="w-5 h-5 text-green-600" /></div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">{tx.success}</h3>
                    <p className="text-slate-500 text-xs">{tx.successDesc}</p>
                  </div>
                ) : (
                  <>
                    {/* Add current product */}
                    <div className="p-5 border-b border-gray-100">
                      <h3 className="text-sm font-bold text-slate-900 mb-4">{tx.addToQuote}</h3>
                      <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-2">{tx.orderType}</p>
                      <div className="flex mb-4">
                        <button onClick={() => setOrderType('pallet')} className={`flex-1 py-2.5 text-xs font-medium border transition-all ${orderType === 'pallet' ? 'border-[#BE1622] bg-[#BE1622] text-white' : 'border-gray-200 text-slate-500'}`}>{tx.pallet}</button>
                        <button onClick={() => setOrderType('truck')} className={`flex-1 py-2.5 text-xs font-medium border border-l-0 transition-all ${orderType === 'truck' ? 'border-[#BE1622] bg-[#BE1622] text-white' : 'border-gray-200 text-slate-500'}`}>{tx.truck}</button>
                      </div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-2">{tx.qty}</p>
                      <div className="flex items-center border border-gray-200 mb-4">
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-2.5 text-slate-400 hover:text-slate-900"><Minus className="w-4 h-4" /></button>
                        <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="flex-1 text-center py-2.5 text-sm font-medium border-x border-gray-200 focus:outline-none" />
                        <button onClick={() => setQuantity(quantity + 1)} className="px-4 py-2.5 text-slate-400 hover:text-slate-900"><Plus className="w-4 h-4" /></button>
                      </div>
                      <button onClick={handleAddToCart} className={`w-full py-3 font-semibold tracking-wider uppercase text-[11px] transition-all flex items-center justify-center gap-2 ${added ? 'bg-green-600 text-white' : 'bg-[#BE1622] hover:bg-[#9E111B] text-white'}`}>
                        {added ? <><Check className="w-4 h-4" /> {tx.added}</> : <><Plus className="w-4 h-4" /> {tx.addToQuote}</>}
                      </button>
                    </div>

                    {/* Cart items */}
                    {items.length > 0 && (
                      <div className="p-5 border-b border-gray-100">
                        <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-3">{tx.yourQuote} ({items.length})</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {items.map((item, i) => (
                            <div key={item.key} className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-900 truncate">{item.productName}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 ${item.orderType === 'pallet' ? 'bg-[#BE1622] text-white' : 'bg-slate-200 text-slate-600'}`}>{item.orderType === 'pallet' ? tx.pallet : tx.truck}</span>
                                  <span className="text-[10px] text-slate-500">x{item.quantity}</span>
                                </div>
                              </div>
                              <button onClick={() => removeItem(item.key)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                        </div>

                        {/* Add more products */}
                        <div className="relative mt-3" ref={pickerRef}>
                          <button type="button" onClick={() => setShowProductPicker(!showProductPicker)}
                            className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-300 hover:border-[#BE1622] text-xs text-slate-500 hover:text-[#BE1622] transition-colors">
                            <Plus className="w-3.5 h-3.5" /> {tx.addMore}
                          </button>
                          {showProductPicker && (
                            <div className="absolute z-50 bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 shadow-lg max-h-52 flex flex-col">
                              <div className="flex items-center border-b border-gray-100 px-3 py-2">
                                <Search className="w-3.5 h-3.5 text-slate-400 mr-2" />
                                <input type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder={tx.searchProd} className="w-full text-xs focus:outline-none" autoFocus />
                              </div>
                              <div className="overflow-y-auto flex-1">
                                {filteredPickerProducts.map(p => (
                                  <button key={p.id} type="button" onClick={() => handleAddFromPicker(p)}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 text-slate-700 flex items-center gap-2">
                                    <img src={p.image_url || ''} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
                                    <span className="truncate">{p[`name_${language}`] || p.name_nl}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Submit toggle */}
                        {!showForm && (
                          <button onClick={() => setShowForm(true)} className="w-full mt-3 bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[10px] py-3 transition-all flex items-center justify-center gap-2">
                            <Send className="w-3.5 h-3.5" /> {tx.sendQuote}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Contact form */}
                    {showForm && items.length > 0 && (
                      <div className="p-5">
                        <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-3">{tx.contact}</p>
                        <form onSubmit={handleSubmit} className="space-y-2.5">
                          <div className="grid grid-cols-2 gap-2.5">
                            <input type="text" required placeholder={tx.firstName} value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" />
                            <input type="text" required placeholder={tx.lastName} value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" />
                          </div>
                          <div className="grid grid-cols-2 gap-2.5">
                            <input type="text" placeholder={tx.company} value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" />
                            <input type="text" placeholder={tx.btw} value={formData.btw} onChange={(e) => setFormData({ ...formData, btw: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" />
                          </div>
                          <div className="grid grid-cols-2 gap-2.5">
                            <input type="email" required placeholder={tx.email} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" />
                            <input type="tel" placeholder={tx.phone} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622]" />
                          </div>
                          <CountrySelect value={formData.country} onChange={(v) => setFormData({ ...formData, country: v })} placeholder={tx.country} />
                          <textarea placeholder={tx.msg} rows={2} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#BE1622] resize-none" />
                          <button type="submit" disabled={sending} className="w-full bg-[#BE1622] hover:bg-[#9E111B] text-white font-semibold tracking-wider uppercase text-[10px] py-3 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                            {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Send className="w-3.5 h-3.5" /> {tx.submit}</>}
                          </button>
                        </form>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {recommended.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-[10px] font-bold text-slate-400 tracking-[0.15em] uppercase mb-4">{tx.recommended}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {recommended.map((rec, ri) => (
                  <Link key={ri} to={`/products/${rec.id}`} className="border border-gray-200 hover:border-[#BE1622]/30 transition-all">
                    <div className="aspect-[4/3] bg-[#F8FAFC] p-4 flex items-center justify-center"><img src={rec.image_url || ''} alt="" className="max-w-full max-h-full object-contain" /></div>
                    <div className="p-3 border-t border-gray-100"><p className="text-xs font-medium text-slate-700">{rec[`name_${language}`] || rec.name_nl}</p></div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
